import React, { useEffect, useState } from "react";
import { fmtDate, fmtDateTime } from "@/lib/dateUtils";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle2, XCircle, Clock4, GraduationCap, ReceiptText } from "lucide-react";
import SnapshotTimeline from "@/components/SnapshotTimeline";

const STATUS_ICONS = {
  present: { Icon: CheckCircle2, color: "text-green-600", label: "Present" },
  absent: { Icon: XCircle, color: "text-red-500", label: "Absent" },
  late: { Icon: Clock4, color: "text-amber-500", label: "Late" },
  excused: { Icon: CheckCircle2, color: "text-neutral-500", label: "Excused" },
};

export default function ParentChildView() {
  const { childId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/parent/child/${childId}`);
      setData(r.data);
    } catch (e) { toast.error("Could not load child overview"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [childId]);

  const sendDigest = async () => {
    setSending(true);
    try {
      await api.post(`/parent/child/${childId}/send-digest`);
      toast.success("Weekly digest emailed to you");
    } catch (e) { toast.error("Could not send digest"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="text-sm text-neutral-500">Loading…</div>;
  if (!data) return <div className="text-sm text-neutral-500">Not found.</div>;

  const { child, teacher_name, upcoming_classes, past_classes, attendance, attendance_summary, fee } = data;

  return (
    <div className="space-y-8" data-testid="parent-child-view">
      <Link to="/parent" className="text-xs text-neutral-500 inline-flex items-center gap-1 hover:text-[#0A0C10]"><ArrowLeft size={12} /> Back</Link>

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ child</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter">{child.full_name}</h1>
        <p className="text-sm text-neutral-600 mt-1 inline-flex items-center gap-2 flex-wrap">
          <GraduationCap size={14} /> {child.grade || "—"} {teacher_name ? <>· Teacher: <strong>{teacher_name}</strong></> : null}
        </p>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        <Stat label="Attendance rate" value={`${attendance_summary.rate_pct}%`} />
        <Stat label="Present" value={attendance_summary.present} />
        <Stat label="Absent" value={attendance_summary.absent} />
        <Stat label="Late" value={attendance_summary.late} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Upcoming classes">
          {upcoming_classes.length === 0 ? <Empty msg="None scheduled." /> :
            upcoming_classes.slice(0, 8).map((c) => (
              <Row key={c.id}>
                <div className="font-medium text-sm">{c.title}</div>
                <div className="text-xs text-neutral-500">{c.subject} · {fmtDateTime(c.start_time)}</div>
              </Row>
            ))}
        </Section>

        <Section title="Recent attendance">
          {attendance.length === 0 ? <Empty msg="No attendance recorded yet." /> :
            attendance.slice(0, 8).map((a) => {
              const meta = STATUS_ICONS[a.status] || STATUS_ICONS.present;
              return (
                <Row key={a.id} data-testid={`att-entry-${a.id}`}>
                  <div className="font-medium text-sm">{fmtDate(a.marked_at)}</div>
                  <div className={`text-xs inline-flex items-center gap-1.5 ${meta.color}`}><meta.Icon size={12} /> {meta.label}</div>
                </Row>
              );
            })}
        </Section>
      </div>

      <SnapshotTimeline studentId={childId} title={`${child.full_name.split(" ")[0]}'s handwritten work`} />

      <Section title="Past classes" wide>
        {past_classes.length === 0 ? <Empty msg="No past classes yet." /> :
          past_classes.slice(0, 12).map((c) => (
            <Row key={c.id}>
              <div className="font-medium text-sm">{c.title}</div>
              <div className="text-xs text-neutral-500">{c.subject} · {fmtDateTime(c.start_time)}</div>
            </Row>
          ))}
      </Section>

      <div className="bg-white border border-neutral-200 p-6 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-3">
          <ReceiptText size={18} className="text-[#002FA7]" />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ fee</div>
            <div className="font-display font-bold text-xl">${(fee.amount || 0).toFixed(0)} <span className="text-sm font-normal text-neutral-500">/ {fee.cycle}</span></div>
          </div>
        </div>
        <button onClick={sendDigest} disabled={sending} data-testid="send-digest-btn" className="text-sm font-semibold border border-neutral-300 hover:border-[#0A0C10] px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60">
          <Mail size={14} /> {sending ? "Sending…" : "Email me a weekly digest"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children, wide }) {
  return (
    <div className={`bg-white border border-neutral-200 p-6 ${wide ? "lg:col-span-2" : ""}`}>
      <div className="font-display font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}
function Row({ children, ...rest }) { return <div {...rest} className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0">{children}</div>; }
function Stat({ label, value }) {
  return (
    <div className="bg-white border border-neutral-200 p-5">
      <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">{label}</div>
      <div className="font-display font-bold text-3xl mt-1">{value}</div>
    </div>
  );
}
function Empty({ msg }) { return <div className="text-sm text-neutral-500 py-6 text-center border border-dashed border-neutral-200">{msg}</div>; }
